import Head from "next/head";
import { useEffect, useState } from "react";
import { server } from "../../config";
import { IProduct } from "../../model/product";
import CheckedBox from "../checked-box/checked-box";
import SimilarProducts from "../similar-products/similar-products";
import CartHandler from "./../../utils/shopping-cart-handler";
import styles from "./product.module.css";
export default function Product({ product,usedInFront }: { product: IProduct,usedInFront?: boolean }) {

  const [similarProducts, setSimilarProducts] = useState<IProduct[]>([]);
  const [productIsInCart, setProductIsInCart] = useState<boolean>(false);
  const [showCheckedBox, setShowCheckedBox] = useState<boolean>(false);
  
  useEffect(() => {
    findSimilarProducts().then();
  }, [product]);


  function checkIfProductIsInCart() {
    const cart = CartHandler.getCart();
    let isInCart = false;
    if (cart && cart.length && product) {
      let prodIndex = cart.findIndex(
        (p: IProduct) => p._id === product._id,
      );
      isInCart = prodIndex > -1;
    }
    setShowCheckedBox(isInCart);
    setProductIsInCart(isInCart);
  }

  const productUrl = `https://sportsam.no/produkter/${encodeURIComponent(product.brand.toLowerCase())}/${encodeURIComponent(product.urlName)}`;
  const jsonLDProductInfo = {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: product.name,
      image: product.image,
      description: product.shortDescription,
      ...(product.gtin ? { gtin13: product.gtin } : {}),
      ...(product.brand ? { brand: {
        "@type": "Brand",
        name: product.brand,
      } } : {}),
      ...(typeof product.price === "number" ? { offers: {
        "@type": "Offer",
        url: productUrl,
        price: product.price.toFixed(2),
        priceCurrency: "NOK",
        availability: `https://schema.org/${product.inStock ? "InStock" : "OutOfStock"}`,
        condition: "https://schema.org/NewCondition",
        seller: {
          "@type": "Organization",
          name: `${product.affiliate[0] + product.affiliate.substring(1).toLowerCase()}`,
          url: product.url,
        },
      } } : {}),
      adult: false,
  };

  const findSimilarProducts = async () => {
    const response = await fetch(
      `${server}/api/product/findSimilar?q=${product?.name}`,
    );
    const res = await response.json();
    setSimilarProducts(res);
  };

  return (
    <>
      {!usedInFront && 
      <>
        <Head>
          <title>{`Sportsam | ${product?.name}`}</title>
          <meta key="description" name="description" content={product.shortDescription} />
          <meta key="og:title" property="og:title" content={`Sportsam | ${product.name}`} />
          <meta key="og:description" property="og:description" content={product.shortDescription} />
          <meta key="og:type" property="og:type" content="product" />
          <meta key="og:url" property="og:url" content={productUrl} />
          {product.image && <meta key="og:image" property="og:image" content={product.image} />}
          <meta name="twitter:card" content="summary_large_image" />
          <link rel="canonical" href={productUrl} />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify([jsonLDProductInfo, {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Hjem", item: "https://sportsam.no/" },
                  { "@type": "ListItem", position: 2, name: product.brand, item: `https://sportsam.no/produkter/${encodeURIComponent(product.brand.toLowerCase())}` },
                  { "@type": "ListItem", position: 3, name: product.name, item: productUrl },
                ],
              }]).replace(/</g, "\\u003c"),
            }}
          />
        </Head>  
        <nav aria-label="Breadcrumbs" className={styles.breadcrumbs}>
          <a href="/">Hjem</a><span aria-hidden="true">/</span>
          <a href={`/produkter/${encodeURIComponent(product.brand.toLowerCase())}`}>{product.brand}</a><span aria-hidden="true">/</span>
          <span aria-current="page">{product.name}</span>
        </nav>
      </>
    }
      <div className={styles.productContainer} >
        <div className={styles.innerContainer}>
          <div>
            <img
              className={styles.productImg}
              src={product?.image}
              alt={product?.name}
            />
          </div>
          <div style={{ textAlign: "left" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <h1>{product.name}</h1>
              {showCheckedBox && <CheckedBox />}
            </div>
            <div
              style={{
                marginBottom: "24px",
                marginTop: "-16px",
              }}
            >
              {product.gtin?.length ? (
                `Artikkelnr. ${product.gtin}`
              ) : (
                <>Artikkelnr. N/A</>
              )}
              <br />
              {product.brand?.length ? (
                `Merke: ${product.brand}`
              ) : (
                <>Merke. Ukjent</>
              )}
            </div>
            <article>
              <strong>Produktbeskrivelse</strong>
              <br />
              <p
                style={{fontSize: "1.1em", lineHeight: "1.5em"}}
                dangerouslySetInnerHTML={{
                  __html:
                  product?.longDescription ||
                    "Ingen beskrivelse fra leverandøren.",
                }}
              ></p>
            </article>

            {product?.affiliatePrices?.length ? (
              <section>
                <strong>Tilgjengelig hos</strong>
                <br />
                <p style={{ fontSize: ".9em" }}>
                  Denne artikkelen er tilgjengelig hos flere leverandører.
                  <br />
                  Prisene kan variere og være avhengige av kampanjer, tilbud,
                  rabatter ol.
                </p>
                <table className={styles.priceCompareTable}>
                  <tbody>
                    <tr>
                      {product.affiliatePrices.map((alt, i) => {
                        return <th key={`th_alt_${i}`}>{alt.affiliate}</th>;
                      })}
                    </tr>
                    <tr>
                      {product.affiliatePrices.map((alt, i) => {
                        return (
                          <td key={`td_alt_${i}`}>
                            <a href={alt.url} rel="nofollow" target="_blank">
                            {alt.price.toLocaleString("no-NO", {
                              style: "currency",
                              currency: "NOK",
                              trailingZeroDisplay: "stripIfInteger",
                            })}
                            </a>
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </section>
            ) : (
              <></>
            )}
            <br />
					<br/>
					{(product.affiliate === 'HYDROFORCE' || (product.affiliatePrices && product.affiliatePrices?.findIndex(a => a.affiliate === 'NYTELSE') > -1)) && 
						<div style={{fontSize: '.9em', fontWeight: 600}}>
							Sportsam.no rabatt tilgjengelig:<br/>
							Ved å benytte rabattkode <strong style={{color: 'var(--primary-color)'}}>A45458</strong> hos Hydro-Force får du 250kr i rabatt ved kjøpt av Sup Brett.<br/>
						</div>
					}
          <br/>            
            <div className={styles.buyBtnWrapper}>
            {
              (product?.affiliate !== "" && product?.affiliate !== "DUMMY") &&
              <a
                href={product?.url}
                title={`Kjøp produktet hos ${product.affiliate}.`}
                className={styles.buyBtn}
                rel="nofollow"
                target="_blank"
              >
                KJØP HOS {product.affiliate} for {" "}
                <span itemProp="price" style={{fontWeight: '600'}}>{product?.price?.toLocaleString("no-NO", {
                  style: "currency",
                  currency: "NOK",
                  trailingZeroDisplay: "stripIfInteger",
                })}</span>
              </a>              
            }

            </div>
            <div
              className={styles.buyBtnWrapper}
              onClick={() => {
                CartHandler.toggleItem(product);
                checkIfProductIsInCart();
                setTimeout(() => {
                  if (!productIsInCart) {
                    const el = document.getElementById("checked-box");
                    if (el) {
                      window.scrollTo(el.offsetLeft, el.offsetTop - 64);
                    }
                  }
                }, 10);
              }}
              style={{
                color: "var(--accent-color)",
                fontWeight: 500,
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {productIsInCart
                ? "- Fjern fra handleliste"
                : "+ Legg til i handleliste"}
            </div>
          </div>
        </div>
      </div>
      {!usedInFront && 
        <div>
          <SimilarProducts products={similarProducts} />
        </div>
      }
    </>
  );
}
